import { NetworkError } from "hooks/usePublicAPI";
import { postGrantRequest } from "pages/Login/login-grant-api";
import { parseGrantCompletion } from "pages/Login/login-grant-flow";

import {
  parseStartedSignIn,
  startSignInFailure,
  startSignUpFailure,
  type SignInFailure,
  type StartedSignIn,
} from "./signIn";

export type StartSignInAnswer =
  | ({ kind: "started" } & StartedSignIn)
  | { kind: "failed"; failure: SignInFailure };

export type StartSignUpAnswer =
  | { kind: "signed_up"; customToken: string }
  | { kind: "failed"; failure: SignInFailure };

function statusOf(err: unknown): number | undefined {
  return err instanceof NetworkError ? err.statusCode : undefined;
}

/** Asks for a code or link to the typed email or phone. The answer has the
 * same shape whether or not anyone is on file for it. `redirect` names the
 * quote the proof should lead to, as `/quotes/<id>`. */
export async function startSignIn(
  identifier: string,
  redirect?: string,
): Promise<StartSignInAnswer> {
  try {
    const raw = await postGrantRequest<unknown>("/auth/start-sign-in", {
      identifier: identifier.trim(),
      ...(redirect === undefined ? {} : { redirect }),
    });
    const started = parseStartedSignIn(raw, redirect);
    return started
      ? { kind: "started", ...started }
      : { kind: "failed", failure: "other" };
  } catch (err) {
    return { kind: "failed", failure: startSignInFailure(statusOf(err)) };
  }
}

/** Creates the account for a channel just proven, with the single-use
 * token the proof handed over. */
export async function startSignUp(input: {
  signupToken: string;
  firstName: string;
  lastName: string;
}): Promise<StartSignUpAnswer> {
  try {
    const raw = await postGrantRequest<unknown>("/auth/start-sign-up", {
      signup_token: input.signupToken,
      first_name: input.firstName.trim(),
      last_name: input.lastName.trim(),
    });
    const completion = parseGrantCompletion(raw);
    return completion.kind === "signed_in"
      ? { kind: "signed_up", customToken: completion.customToken }
      : { kind: "failed", failure: "other" };
  } catch (err) {
    return { kind: "failed", failure: startSignUpFailure(statusOf(err)) };
  }
}
